import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginService } from '@ebw-monorepo/login';
import { EbwModalService } from '@ebw-monorepo/modal';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';
import { MapService } from './map.service';
import { SplitViewService } from '../../../../utils/src/lib/services/split-view.service';

/**
 * Service for handling the PSA actions, events, and entities.
 * This service interacts with various endpoints to manage actions,
 * events, and entities related to Public Service Announcements (PSA).
 *
 * @author Tommaso Lazzaretti
 */
@Injectable({
  providedIn: 'root'
})
export class PsaService {
  isVisibleDuplicatePsa = false;
  typeDuplicatePsa: 'MATRICE_RISCHIO' | 'CHECKLIST' = null;
  psaFrom = null;

  // nodo di partenza per creazione interconnessione
  startNode$ = new BehaviorSubject(null);

  isVisibleCreateInterconnection = false;

  // emesso quando un nodo viene creato/modificato/eliminato, cosi le liste di ricerca aperte altrove possono aggiornarsi
  nodeChanged$ = new Subject<void>();

  // emesso dopo il salvataggio delle risposte di una checklist, che concorrono allo stato di avanzamento
  checklistChanged$ = new Subject<void>();

  constructor(
    private _http: HttpClient,
    private _modalService: EbwModalService,
    private _loginService: LoginService,
    private _apiUrlService: ApiUrlService,
    private _mapService: MapService,
    private _splitViewService: SplitViewService
  ) {}

  private static readonly DUPLICATE_CLICK_OWNER_ID = 'psa-duplicate';

  /** La duplica PSA è un tool "strong" del click sulla mappa (usa interaction di disegno e selezione) */
  private _acquireDuplicateClick(): boolean {
    return this._mapService.acquireMapClick({ id: PsaService.DUPLICATE_CLICK_OWNER_ID, level: 'strong' });
  }

  get _headers(): HttpHeaders {
    // Get the Authorization header with the JWT token retrieved from the login service
    return new HttpHeaders({
      Authorization: 'Bearer ' + this._loginService.getToken()
    });
  }

  isUrnReteIdrica(urn: string) {
    return urn?.includes('rete_idrica') ?? false;
  }

  isUrnSchemaIdropotabile(urn: string) {
    return urn?.includes('schema_idropotabile') ?? false;
  }

  isPsaObject(urn: string) {
    return this.isUrnReteIdrica(urn) || this.isUrnSchemaIdropotabile(urn);
  }

  /**
   * show duplicate modal
   */
  toggleDuplicatePsa() {
    if (this.isVisibleDuplicatePsa) {
      this.hideDuplicatePsa();
    } else if (this._acquireDuplicateClick()) {
      this.isVisibleDuplicatePsa = true;
    }
  }

  /**
   * show duplicate modal and set the current type
   */
  showDuplicatePsaType(type: 'MATRICE_RISCHIO' | 'CHECKLIST'): boolean {
    if (!this._acquireDuplicateClick()) {
      return false;
    }
    this.isVisibleDuplicatePsa = true;
    this.typeDuplicatePsa = type;
    return true;
  }

  /**
   * hide duplicate modal
   */
  hideDuplicatePsa() {
    this.isVisibleDuplicatePsa = false;
    this.typeDuplicatePsa = null;
    this._mapService.releaseMapClick(PsaService.DUPLICATE_CLICK_OWNER_ID);
  }

  /**
   * open the create interconnection panel
   */
  openCreateInterconnectionPanel(startNode) {
    this.startNode$.next(startNode);
    this.isVisibleCreateInterconnection = true;
  }

  /**
   * close the create interconnection panel
   */
  closeCreateInterconnectionPanel() {
    this.startNode$.next(null);
    this.isVisibleCreateInterconnection = false;
  }

  /**
   * Set psa Object for duplicateModal
   *
   * @param psa
   */
  setPsaToBeDuplicated(psa) {
    this.psaFrom = psa;
  }

  /**
   * Open the split view panel to duplicate the checklist or the risk matrix of an element
   *
   * @param psa element to copy from (needs id, tipologia, search_term)
   * @param type what to duplicate
   */
  openDuplicatePsa(psa, type: 'MATRICE_RISCHIO' | 'CHECKLIST') {
    // c'è già uno strumento aperto: il layout non va modificato
    if (!this.showDuplicatePsaType(type)) {
      return;
    }
    this.setPsaToBeDuplicated(psa);
    this._splitViewService.layoutConfChange({ isEnable: true, halfScreen: true, collapsed: true });
  }

  /**
   * Fetches the list of all PSA actions.
   *
   * @returns Observable containing the list of actions.
   */
  public _getListActions(nome?): Observable<any> {
    const url = this._apiUrlService.createUrl(API_URLS.PSA.LIST_ACTION);
    return this._http.get(nome ? `${url}?nome=${nome}` : `${url}`, { headers: this._headers });
  }

  /**
   * Adds a new PSA action.
   *
   * @param params - The action data to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _addAction(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.ADD_ACTION), params, {
      headers: this._headers
    });
  }

  /**
   * Fetches details of a specific PSA action.
   *
   * @param params - Optional query parameters to filter the action.
   * @returns Observable containing the details of the action.
   */
  public _getAction(params?): Observable<any> {
    let queryParams = '';
    const url = this._apiUrlService.createUrl(API_URLS.PSA.GET_ACTION);

    if (params) {
      queryParams = Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
    }

    return this._http.get(queryParams ? `${url}?${queryParams}` : url, { headers: this._headers });
  }

  /**
   * Delete a PSA action.
   *
   * @param id - The action id to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _deleteAction(id: string): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.DELTE_ACITON)}?id=${id}`, {
      headers: this._headers
    });
  }

  /**
   * Attaches a PSA action to an event.
   *
   * @param params - The action-event data to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _attachActionToEvent(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.ATTACH_ACTION_TO_EVENT), params, {
      headers: this._headers
    });
  }

  /**
   * Fetches the list of all PSA events.
   *
   * @param params - The params (nome, tipologia) of Event.
   * @returns Observable containing the list of events.
   */
  public _getListEvents(params?: { nome?: string; tipologia?: string }): Observable<any> {
    const baseUrl = this._apiUrlService.createUrl(API_URLS.PSA.LIST_EVENTS);
    const queryParams = new URLSearchParams();

    // Add parameters only if they exist and are not empty
    if (params?.nome) {
      queryParams.append('nome', params.nome);
    }
    if (params?.tipologia) {
      queryParams.append('tipologia', params.tipologia);
    }

    return this._http.get(queryParams.toString() ? `${baseUrl}?${queryParams}` : baseUrl, { headers: this._headers });
  }

  /**
   * Adds a new PSA event.
   *
   * @param params - The event data to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _addEvent(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.ADD_EVENT), params, {
      headers: this._headers
    });
  }

  /**
   * Update a PSA rel event relation with Entity.
   *
   * @param params - The event data to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _updateRelEvent(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.UPDATE_REL_ENTITY_EVENTO_RISCHIO), params, {
      headers: this._headers
    });
  }

  /**
   * Update a PSA event.
   *
   * @param params - The event data to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _updateEvent(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.UPDATE_EVENT), params, {
      headers: this._headers
    });
  }

  /**
   * Delete a PSA event.
   *
   * @param id - The event id to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _deleteEvent(id): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.DELETE_EVENT)}?id=${id}`, {
      headers: this._headers
    });
  }

  /**
   * Fetches details of a specific PSA event.
   *
   * @param params - Optional query parameters to filter the event.
   * @returns Observable containing the details of the event.
   */
  public _getEvent(params?): Observable<any> {
    let queryParams = '';
    const url = this._apiUrlService.createUrl(API_URLS.PSA.GET_EVENT);

    if (params) {
      queryParams = Object.keys(params)
        .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
    }

    return this._http.get(queryParams ? `${url}?${queryParams}` : url, { headers: this._headers });
  }

  /**
   * Fetches all actions associated with a specific PSA event.
   *
   * @returns Observable containing the list of actions for the event.
   */
  public _getEventActions(idEvent): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.GET_EVENT_ACTIONS)}?id=${idEvent}`, {
      headers: this._headers
    });
  }

  /**
   * Attaches a PSA event to multiple entities.
   *
   * @returns Observable containing the server's response.
   */
  public _attachEventToEntities(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.ATTACH_EVENT_TO_ENTITIES), params, {
      headers: this._headers
    });
  }

  /**
   * Attaches multiple PSA events to an entity.
   *
   * @returns Observable containing the server's response.
   */
  public _attachEventsToEntity(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.ATTACH_EVENTS_TO_ENTITY), params, {
      headers: this._headers
    });
  }

  /**
   * Fetches a list of PSA entities based on a search term.
   *
   * @param term - The search term to filter entities.
   * @param type - selected type to filter
   * @returns Observable containing the list of matching entities.
   */
  public _getListElements(term?: string, tipologia?: string, type?: 'nodo' | 'interconnessione' | 'wsz'): Observable<any> {
    let baseUrl = this._apiUrlService.createUrl(API_URLS.PSA.LIST_ENTITIES);

    baseUrl = baseUrl.concat(`?term=${term}`);

    if (tipologia) {
      baseUrl = baseUrl.concat(`&tipologia=${tipologia}`);
    }

    if (type) {
      baseUrl = baseUrl.concat(`&type=${type}`);
    }

    return this._http.get(baseUrl, {
      headers: this._headers
    });
  }

  /**
   * Fetches the details of a specific PSA entity.
   *
   * @returns Observable containing the entity details.
   */
  public _getDetail(id): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.GET_DETAIL)}?id=${id}`, {
      headers: this._headers
    });
  }

  /**
   * Attaches a PSA action to a specific entity.
   *
   * @returns Observable containing the server's response.
   */
  public _attachActionToEntity(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.ATTACH_ACTION_TO_ENTITY), params, {
      headers: this._headers
    });
  }

  /**
   * Removes a PSA event from a specific entity.
   *
   * @returns Observable containing the server's response.
   */
  public _removeEventFromEntity(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.REMOVE_EVENT_FROM_ENTITY), params, {
      headers: this._headers
    });
  }

  /**
   * Removes PSA events from a specific entity.
   *
   * @returns Observable containing the server's response.
   */
  public _removeMultipleEventsFromEntity(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.REMOVE_MULTIPLE_EVENTS_FROM_ENTITY), params, {
      headers: this._headers
    });
  }

  /**
   * Removes a PSA action from a specific entity.
   *
   * @returns Observable containing the server's response.
   */
  public _removeActionFromEntity(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.REMOVE_ACTION_FROM_ENTITY), params, {
      headers: this._headers
    });
  }

  /**
   * Duplicates the risks matrix for a PSA entity.
   *
   * @returns Observable containing the server's response.
   */
  public _duplicateRisksMatrix(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.DUPLICATE_RISK_MATRIX), params, {
      headers: this._headers
    });
  }

  /**
   * Bulk upsert of the risk matrix (events + mitigation actions) for a PSA entity.
   *
   * @param params - { entityId, events } payload built by risk-matrix.mapper.ts
   * @returns Observable containing the server's response.
   */
  public _bulkUpsertRiskMatrix(params: { entityId: string; events: unknown[] }): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.BULK_MATRIX_INSERT), params, {
      headers: this._headers
    });
  }

  /**
   * Duplicates all the checklist for a PSA entity.
   *
   * @returns Observable containing the server's response.
   */
  public _duplicateChecklist(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.DUPLICATE_CHECK_LIST), params, {
      headers: this._headers
    });
  }

  /**
   * Fetches the list of all PSA areas.
   *
   * @returns Observable containing the list of areas.
   */
  public _getListAreas(nome?): Observable<any> {
    const url = this._apiUrlService.createUrl(API_URLS.PSA.LIST_AREAS);
    return this._http.get(nome ? `${url}?nome=${nome}` : `${url}`, { headers: this._headers });
  }

  public _getEntityTypes(type?: string): Observable<any> {
    const url = this._apiUrlService.createUrl(API_URLS.PSA.GET_ENTITY_TYPES);
    return this._http.get(type ? `${url}?type=${type}` : url, {
      headers: this._headers
    });
  }

  public _selectEntitiesRisksMatrix(lng, lat, hit_radius, tipologia: string): Observable<any> {
    return this._http.get(
      `${this._apiUrlService.createUrl(API_URLS.PSA.SELECT)}?lng=${lng}&lat=${lat}&hit_radius=${hit_radius}&tipologia=${tipologia}`,
      { headers: this._headers }
    );
  }

  private _checklistElementTypes$: Observable<string[]>;

  /**
   * Element types with a checklist. They change only when the ministerial checklists are reloaded,
   * so the list is requested once and shared by every element.
   */
  public _getChecklistElementTypes(): Observable<string[]> {
    if (!this._checklistElementTypes$) {
      this._checklistElementTypes$ = this._http
        .get<string[]>(this._apiUrlService.createUrl(API_URLS.PSA.GET_CHECKLIST_ELEMENT_TYPES), { headers: this._headers })
        .pipe(shareReplay(1));
    }
    return this._checklistElementTypes$;
  }

  public _getCheckList(entityId): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.GET_CHECKLIST)}?entity_id=${entityId}`, {
      headers: this._headers
    });
  }

  public _getCheckListAnswers(entity_id): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.GET_CHECKLIST_ANSWERS)}?entity_id=${entity_id}`, { headers: this._headers });
  }

  public _upsertCheckListAnswers(params): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.UPSERT_CHECKLIST_ANSWERS), params, {
      headers: this._headers
    });
  }

  public _getStatoAvanzamento(id): Observable<any> {
    return this._http.get(`${this._apiUrlService.createUrl(API_URLS.PSA.STATO_AVANZAMENTO)}?id=${id}`, {
      headers: this._headers
    });
  }

  public _getMatriceRischio(id): Observable<any> {
    let url = this._apiUrlService.createUrl(API_URLS.PSA.MATRICE_RISCHIO);
    id && (url += `?id=${id}`);
    return this._http.get(url, { headers: this._headers });
  }

  public _exportMatriceRischio(id?: number): Observable<any> {
    let url = this._apiUrlService.createUrl(API_URLS.PSA.EXPORT_MATRICE_RISCHIO);
    id && (url += `?id=${id}`);
    return this._http.get(url, { headers: this._headers, responseType: 'blob' }).pipe(
      map((data) => {
        const file = new Blob([data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        return fileURL;
      })
    );
  }

  /**
   * Export the risk matrix in Excel format.
   * @param id
   */
  public _exportExcelMatriceRischio(params: { id?: number; area_psa_id?: number; nomeFile: string; downloadInFilesystem: boolean }): Observable<any> {
    let url = this._apiUrlService.createUrl(API_URLS.PSA.EXPORT_EXCEL_MATRICE_RISCHIO);
    // aggiungo o id dell'eleemnto dello schema idropotabile o l'id dell'area psa
    params.id && (url += `?id=${params.id}`);
    params.area_psa_id && (url += `?area_psa_id=${params.area_psa_id}`);
    // aggiungo il nome del file
    url += `&nomeFile=${params.nomeFile}`;
    // flag che indica se restituire il file a frontend per scaricarlo oppure se crearlo e posizionarlo nel filesystem
    url += `&downloadInFilesystem=${params.downloadInFilesystem}`;
    return this._http.get(url, { headers: this._headers, responseType: 'blob' }).pipe(
      map((data) => {
        const file = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        return URL.createObjectURL(file);
      })
    );
  }

  /**
   * Export the risk matrix in Excel format.
   * @param id
   */
  public _downloadPdfChecklistInFilesystem(body: { content: any; styles: any; elementId?: number; nomeFile: string }): Observable<any> {
    let url = this._apiUrlService.createUrl(API_URLS.PSA.SAVE_PDF_CHECKLIST);
    return this._http.post(url, body, { headers: this._headers });
  }

  openMatriceRischio(item: any, downloadInFilesystem: boolean, isAreaPsa = false) {
    const timestamp = this.getFormattedDate();
    const nomeFile = `Matrice_di_Rischio_${item.nome}_${timestamp}.xlsx`; // Nome del file
    let payload: any = { nomeFile, downloadInFilesystem };
    payload = isAreaPsa ? { ...payload, area_psa_id: item.id } : { ...payload, id: item.id };
    this._exportExcelMatriceRischio(payload).subscribe((data) => {
      if (downloadInFilesystem) {
        this._modalService.openInfoModal({
          title: 'Matrice di rischio',
          content: nomeFile + ' aggiunto con successo'
        });
        return;
      }

      // Se il file non viene scaricato lo mostro a video
      const a = document.createElement('a');
      a.href = data;
      a.download = nomeFile; // Nome del file
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(data);
    });
  }

  /**
   * Update a PSA rel event relation with Entity.
   *
   * @param params - The event data to be sent to the server.
   * @returns Observable containing the server's response.
   */
  public _updateRelEntityEventoRischioAzioneMitigatrice(params: { id: string; stato: number }): Observable<any> {
    return this._http.post(this._apiUrlService.createUrl(API_URLS.PSA.UPDATE_REL_ENTITY_EVENTO_RISCHIO_STATE), params, {
      headers: this._headers
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  createInterconnection(body: { nodo1: string; nodo2: string; nome?: string }) {
    const url = this._apiUrlService.createUrl(API_URLS.PSA.CREATE_INTERCONNECTION);
    return this._http.post(url, body, {
      headers: this._headers
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  deleteInterconnection(id: string) {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.DELETE_INTERCONNECTION)}/${id}`;
    return this._http.delete(url, {
      headers: this._headers
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  addNode(body: { nome: string; tipologia: string; lat: number; lon: number; eventiRischioIds?: string[] }) {
    const url = this._apiUrlService.createUrl(API_URLS.PSA.ADD_NODE);
    return this._http.post(url, body, {
      headers: this._headers
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  updateNode(id: number, body: { nome?: string; lat?: number; lon?: number; eventiRischioIds?: string[] }) {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.UPDATE_NODE)}/${id}`;
    return this._http.put(url, body, {
      headers: this._headers
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  deleteNode(id: number) {
    const url = `${this._apiUrlService.createUrl(API_URLS.PSA.DELETE_NODE)}/${id}`;
    return this._http.delete(url, {
      headers: this._headers
    });
  }

  // ------------------------------------------------------------------------------------------------------------

  getFormattedDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Mesi da 0 a 11
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}_${hours}${minutes}`;
  }
}
